import React, { useEffect, useState } from "react";
import GenericList from "../shared/components/GenericList";
import { GridColDef } from "@mui/x-data-grid";
import { Grid2 } from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
} from "recharts";
import { fisyTricicleUrl } from "../../configs/URL";

export interface FicheTricycle {
  id: number;
  Daty: string;
  Lalana: string;
  Km_fiaingana: number;
  Km_niafarana: number;
  lasantsy_nampidirina: number;
  isan_ny_voyage_vita: number;
  Km_total?: number; // Calculé côté front
  Rendement?: number; // km parcourus par litre
}

export const FicheTricycleTable: React.FC = () => {
  const [data, setData] = useState<FicheTricycle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Remplace ici par ton endpoint réel
        const res = await fetch(fisyTricicleUrl);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();

        const mapped: FicheTricycle[] = json.results.map((r: any, index: number) => {
          const Km_fiaingana = Number(r.Km_fiaingana) || 0;
          const Km_niafarana = Number(r.Km_niafarana) || 0;
          const lasantsy = Number(r.lasantsy_nampidirina) || 0;
          const kmTotal = Km_niafarana - Km_fiaingana;
          const rendement = lasantsy > 0 ? kmTotal / lasantsy : 0;

          return {
            id: r._id || index,
            Daty: r.Daty,
            Lalana: r.Lalana,
            Km_fiaingana,
            Km_niafarana,
            lasantsy_nampidirina: lasantsy,
            isan_ny_voyage_vita: Number(r.isan_ny_voyage_vita) || 0,
            Km_total: kmTotal,
            Rendement: Number(rendement.toFixed(2)),
          };
        });

        setData(mapped);
      } catch (err: any) {
        setError(err.message || "Erreur de chargement");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <div>Chargement…</div>;
  if (error) return <div style={{ color: "red" }}>Erreur: {error}</div>;

  const columns: GridColDef[] = [
    { field: "Daty", headerName: "Daty (Date)", width: 120 },
    { field: "Lalana", headerName: "Lalana (Itinéraire)", width: 200 },
    { field: "Km_fiaingana", headerName: "Km fiaingana", width: 130, type: "number" },
    { field: "Km_niafarana", headerName: "Km niafarana", width: 130, type: "number" },
    { field: "Km_total", headerName: "Km total", width: 120, type: "number" },
    { field: "lasantsy_nampidirina", headerName: "Lasantsy (L)", width: 140, type: "number" },
    { field: "Rendement", headerName: "Rendement (km/L)", width: 160, type: "number" },
    { field: "isan_ny_voyage_vita", headerName: "Voyages", width: 120, type: "number" },
  ];

  return (
    <div style={{ padding: "16px" }}>
      <Grid2 container spacing={2} style={{ marginBottom: "16px" }}>
        <Grid2 size={6}>
          <h3 style={{ textAlign: "center", marginBottom: 10 }}>
            Distance totale vs essence utilisée
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Daty" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="Km_total" name="Km total" fill="#82ca9d" />
              <Bar dataKey="lasantsy_nampidirina" name="Essence (L)" fill="#ff8042" />
            </BarChart>
          </ResponsiveContainer>
        </Grid2>

        <Grid2 size={6}>
          <h3 style={{ textAlign: "center", marginBottom: 10 }}>
            Rendement (km/L) par jour
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="Daty" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="Rendement" stroke="#8884d8" name="Rendement" />
            </LineChart>
          </ResponsiveContainer>
        </Grid2>
      </Grid2>

      <GenericList columns={columns} rows={data} />
    </div>
  );
};

export default FicheTricycleTable;
